import type { Tables, TablesInsert } from "@/types/database";

import { supabase } from "@/lib/supabase/client";
import { getUserFriendlyError, isDevelopment } from "@/lib/utils/errorMessages";
import { getErrorMessage } from "@/lib/utils/errorUtils";

// Define session type locally to avoid direct Supabase imports
interface AuthSessionData {
  access_token: string;
  token_type: string;
  expires_in: number;
  expires_at?: number;
  refresh_token: string;
  user: {
    id: string;
    email?: string;
    [key: string]: string | number | boolean | null | undefined;
  };
}

type Profile = Tables<'profiles'>;
// UserProfile is just an alias for Profile (already has all fields)
type UserProfile = Profile & {
  avatarUrl?: string;  // Additional UI field
};

function isAbortLikeError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  if (error instanceof Error) {
    return error.name === 'AbortError' || error.message === 'Failed to fetch';
  }

  if (typeof error === 'object' && error !== null) {
    const maybeMessage = 'message' in error ? error.message : undefined;
    if (typeof maybeMessage === 'string') {
      return maybeMessage === 'Failed to fetch' || maybeMessage.includes('AbortError');
    }
  }

  return false;
}

export async function getCurrentUser() {
  try {
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      throw error;
    }

    if (!data.session) {
      return {
        authenticated: false,
        user: null,
        error: null
      };
    }

    const userId = data.session.user.id;
    const userProfile = await getUserProfile(userId);

    return {
      authenticated: true,
      user: {
        ...data.session.user,
        ...userProfile
      },
      error: null
    };
  } catch (error) {
    console.error('Error getting current user:', error);
    const errorMessage = getErrorMessage(error);
    return {
      authenticated: false,
      user: null,
      error: errorMessage
    };
  }
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    // First check if the current user is anonymous using session
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.is_anonymous) {
      // Anonymous users don't have profiles, return null immediately
      return null;
    }

    const result = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (result.error) {
      // If no rows found (PGRST116), return null without logging error
      if (result.error.code === 'PGRST116') {
        return null;
      }
      throw result.error;
    }

    return result.data as UserProfile;
  } catch (error) {
    // Route changes can abort in-flight profile fetches; treat those as non-fatal.
    if (isAbortLikeError(error)) {
      return null;
    }

    console.error("Error fetching user profile:", error);
    return null;
  }
}

export async function upsertUserProfile(profile: Partial<UserProfile>): Promise<UserProfile | null> {
  try {
    const result = await supabase
      .from("profiles")
      .upsert(profile as TablesInsert<'profiles'>)
      .select()
      .single();

    if (result.error) {
      throw result.error;
    }

    return result.data as UserProfile;
  } catch (error) {
    console.error("Error upserting user profile:", error);
    return null;
  }
}

export async function createUserProfile(userData: {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  plan?: string;
}): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("profiles")
      .insert({
        id: userData.id,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        plan: userData.plan ?? "none",
        role: "host", // Default role for signed-up users
        created_at: new Date().toISOString() // Add created_at field
      });

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error creating user profile:", error);

    // In test/development environments, log but don't throw
    if (isDevelopment()) {
      console.warn("Profile creation failed, but continuing for testing/development purposes");
      return false;
    }

    throw new Error(getUserFriendlyError(error as Error, { context: 'createUserProfile', logError: false }));
  }
}

export async function updateUserPlan(userId: string, plan: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("profiles")
      .update({ plan })
      .eq("id", userId);

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error updating user plan:", error);
    return false;
  }
}

export async function getCurrentSession() {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return { success: true, session: data.session, error: null };
  } catch (error) {
    console.error('Error getting session:', error);
    return { success: false, session: null, error: getErrorMessage(error) };
  }
}

/**
 * Returns the currently authenticated user, awaiting client hydration and
 * network-validating the token. Prefer this over getCurrentSession() when
 * deciding whether to create a session — getSession() reads local storage and
 * is documented as unreliable in cookie/SSR contexts, which caused returning
 * guests to mint a new anonymous user on reload. Returns user: null when there
 * is genuinely no session (do NOT treat the absence of an error as a session).
 */
export async function getValidatedUser() {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) {
      // "Auth session missing" is the normal "no session" signal — not an error to log loudly.
      return { success: false, user: null, error: getErrorMessage(error) };
    }
    return { success: true, user: data.user, error: null };
  } catch (error) {
    return { success: false, user: null, error: getErrorMessage(error) };
  }
}

export async function exchangeCodeForSession(code: string) {
  try {
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw error;
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error exchanging code for session:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function signInWithPassword(email: string, password: string) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error signing in:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function signUpWithPassword(email: string, password: string, options?: { data?: Record<string, string | number | boolean> }) {
  try {
    const { data, error } = await supabase.auth.signUp({ email, password, options });
    if (error) throw error;
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error signing up:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function signInWithOAuth(provider: 'google' | 'github', options?: { redirectTo?: string; queryParams?: Record<string, string>; skipBrowserRedirect?: boolean }) {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({ provider, options });
    if (error) throw error;
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error signing in with OAuth:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function signInWithMagicLink(email: string, options?: { redirectTo?: string; data?: Record<string, unknown> }) {
  try {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: options?.redirectTo,
        shouldCreateUser: true,
        data: options?.data,
      },
    });
    if (error) throw error;
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error signing in with magic link:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error signing out:', error);
    return { success: false, error: getErrorMessage(error) };
  }
}

export async function signInAnonymously() {
  try {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) throw error;
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error signing in anonymously:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function resetPassword(email: string) {
  try {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error resetting password:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function updatePassword(currentPassword: string, newPassword: string) {
  try {
    // First, get the current user's email
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const email = userData.user?.email;
    if (!email) {
      throw new Error('Unable to verify identity: no email found');
    }

    // Reauthenticate with current password to verify identity
    const { error: reauthError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });

    if (reauthError) {
      if (reauthError.status === 400 || reauthError.message?.toLowerCase().includes('invalid')) {
        throw new Error('Current password is incorrect');
      }
      throw reauthError;
    }

    // Now update to the new password
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;

    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error updating password:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export function onAuthStateChange(callback: (event: string, session: AuthSessionData | null) => void) {
  return supabase.auth.onAuthStateChange((event, session) => {
    // Convert Supabase session to our AuthSessionData format
    const authSession: AuthSessionData | null = session ? {
      access_token: session.access_token,
      token_type: session.token_type ?? 'bearer',
      expires_in: session.expires_in ?? 3600,
      expires_at: session.expires_at,
      refresh_token: session.refresh_token,
      user: {
        id: session.user.id,
        email: session.user.email,
        ...session.user.user_metadata
      }
    } : null;
    
    callback(event, authSession);
  });
}

export async function updateUserMetadata(metadata: Record<string, string | number | boolean>) {
  try {
    const { data, error } = await supabase.auth.updateUser({ data: metadata });
    if (error) throw error;
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error updating user metadata:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function getUserCollectionsCount(userId: string) {
  try {
    const { count, error } = await supabase
      .from('collections')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId);
    
    if (error) throw error;
    return { success: true, count: count ?? 0, error: null };
  } catch (error) {
    console.error('Error getting collections count:', error);
    return { success: false, count: 0, error: getErrorMessage(error) };
  }
}

export async function getUserOnboardingStatus(userId: string) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('onboarding_completed')
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: true, onboardingCompleted: false, error: null };
      }
      throw error;
    }

    const profile = data as Pick<Profile, 'onboarding_completed'> | null;
    return { success: true, onboardingCompleted: profile?.onboarding_completed ?? false, error: null };
  } catch (error) {
    console.error('Error getting onboarding status:', error);
    return { success: false, onboardingCompleted: false, error: getErrorMessage(error) };
  }
}

export async function updateOnboardingStatus(userId: string, completed: boolean) {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ onboarding_completed: completed })
      .eq('id', userId);
    
    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating onboarding status:', error);
    return { success: false, error: getErrorMessage(error) };
  }
}

export async function verifyOtp(tokenHash: string, type: 'email' | 'email_change' = 'email') {
  try {
    // Handle different OTP verification types with proper typing
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type,
    });
    if (error) throw error;
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function getCurrentAuthUser() {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return { success: true, user: data.user, error: null };
  } catch (error) {
    console.error('Error getting current auth user:', error);
    return { success: false, user: null, error: getErrorMessage(error) };
  }
}

export async function updateUserRole(userId: string, role: string) {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ role })
      .eq('id', userId);
    
    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating user role:', error);
    return { success: false, error: getErrorMessage(error) };
  }
}

export async function getUserSubscriptionInfo(userId: string, signal?: AbortSignal) {
  try {
    // Check if already aborted
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    let query = supabase
      .from('profiles')
      .select('subscription_status, stripe_price_id, plan, plan_tier, current_period_end')
      .eq('id', userId);

    // Add abort signal if provided
    if (signal) {
      query = query.abortSignal(signal);
    }

    const { data, error } = await query.single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: true, data: null, error: null };
      }
      throw error;
    }

    return { success: true, data, error: null };
  } catch (error) {
    // Re-throw abort errors so callers can handle them
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    console.error('Error getting user subscription info:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function getUserStorageInfo(userId: string) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('used_storage_gb, total_storage_gb')
      .eq('id', userId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return { success: true, data: null, error: null };
      }
      throw error;
    }
    
    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error getting user storage info:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}

export async function getUserPlanTier(userId: string) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('plan_tier')
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: true, data: null, error: null };
      }
      throw error;
    }

    return { success: true, data: data?.plan_tier ?? null, error: null };
  } catch (error) {
    console.error('Error getting user plan_tier:', error);
    return { success: false, data: null, error: getErrorMessage(error) };
  }
}
